/**
 * Partners — Sponsors & partners section (Home). A lightweight preview of the
 * dedicated /partners page: same terminology and tier logic, confirmed
 * partners only (no fake sponsors, no empty "awaiting" bays), and an honest
 * "Partnership opportunities available" invitation with a Become-a-partner CTA
 * to the Contact page.
 *
 * Tiers (top → bottom, decreasing prominence): Strategic Partners, Gold,
 * Silver, Bronze. Only tiers that have at least one confirmed partner render
 * as a row; open tiers are represented by the invitation below the pyramid.
 *
 * The outreach logo wall (<OutreachPartners />) sits under the pyramid and is
 * the same component the /partners page uses, so both stay in sync.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import OutreachPartners from './OutreachPartners';
import '../ComponentsStyling/Partners.css';

/* ------------------------------------------------------------------ */
/* Tier configuration                                                  */
/* ------------------------------------------------------------------ */

const TIERS = [
    { key: 'diamond',  label: 'Diamond',  serial: '01' },
    { key: 'emerald',  label: 'Emerald',  serial: '02' },
    { key: 'platinum', label: 'Platinum', serial: '03' },
    { key: 'gold',     label: 'Gold',     serial: '04' },
    { key: 'silver',   label: 'Silver',   serial: '05' },
    { key: 'bronze',   label: 'Bronze',   serial: '06' },
];

/* Named partner roles — a different axis from the tiers above, so they are NOT
   rungs on the pyramid. Each one simply states its own role underneath the
   logo. Roles and roster come from the partnership sheet; anyone on it without
   a logo file is left out rather than shown as a name-only tile. Mirrors
   /partners. Tones measured from the artwork, as with the tiers below:
     Quill — white wordmark + teal leaf on alpha (dark)
     Future Skills Fund — pale Arabic lockup + bright blue wordmark (dark)
     Mindova — neon purple glyph with a glow halo a plate would flatten (dark)
     Arab Advisors Group — #005A96 type on alpha, built for white (plate)
     Qafza — a JPEG on an opaque white canvas, so the plate is mandatory
     Qodra AI — a white mark on alpha (dark)
     Taziz — navy + purple on an opaque white canvas (plate) */
const ROLE_PARTNERS = [
    { name: 'Quill', role: 'Robotics Sponsor', logo: '/images/partners/Quill.png', tone: 'dark', href: '' },
    { name: 'Future Skills Fund', role: 'Opportunity & Financial Support Partner', logo: '/images/partners/futureSkillFund.png', tone: 'dark', href: '' },
    { name: 'Mindova', role: 'Innovation Partner', logo: '/images/partners/MindovaLogo.png', tone: 'dark', href: '' },
    { name: 'Arab Advisors Group', role: 'Research Partner', logo: '/images/partners/ArabAdvisorGroup.png', tone: 'plate', href: '' },
    { name: 'Qafza', role: 'Learning & Development Partner', logo: '/images/partners/qafza.jpeg', tone: 'plate', href: '' },
    /* The sheet leaves the codename blank for these two, so they keep the
       Participant label they already had rather than an invented one. */
    { name: 'Qodra AI', role: 'Participant Partner', logo: '/images/partners/QodraAi.png', tone: 'dark', href: '' },
    { name: 'Taziz', role: 'Participant Partner', logo: '/images/partners/taziz.png', tone: 'plate', href: '' },
    /* Snacks. Mr. Chips is a full-colour badge with a white keyline, so it
       holds up on the dark ground. Baker is brown line art that would vanish
       there, and the Indomie / Pop Mie sticker needs its navy outline to read
       as an edge rather than melt into the card — both get the plate. */
    { name: 'Mr. Chips', role: 'Snacks Partner', logo: '/images/partners/MrChips.png', tone: 'dark', href: '' },
    { name: 'Baker', role: 'Snacks Partner', logo: '/images/partners/baker.png', tone: 'plate', href: '' },
    { name: 'Indomie & Pop Mie', role: 'Snacks Partner', logo: '/images/partners/LogoIndomiePopMie.png', tone: 'plate', href: '' },
    /* Good Day is a saturated red wordmark (ink luminance 57) and Intaj a deep
       blue one (70) — both are dark ink on transparency, so both take a plate.
       Bidaya is the opposite case: a white wordmark with gold accents, which a
       plate would erase, so it stays on the dark ground. */
    { name: 'Good Day', role: 'Snacks Partner', logo: '/images/partners/goodDay.png', tone: 'plate', href: '' },
    { name: 'Bidaya Water', role: 'Snacks Partner', logo: '/images/partners/BidayaWater.png', tone: 'dark', href: '' },
    { name: 'Intaj', role: 'Community Partner', logo: '/images/partners/Intag.png', tone: 'plate', href: '' },
];

/* ------------------------------------------------------------------ */
/* Confirmed partners — real, verified only. Mirrors the /partners page. */
/*                                                                      */
/* `tone` says what the logo file itself needs behind it, which was      */
/* measured from the artwork rather than guessed:                        */
/*   'plate' — the mark is dark ink (and often on a baked-in white       */
/*             background), so it needs a light plate or it reads as a   */
/*             white box / disappears into the dark surface.             */
/*   'dark'  — the mark is white or light and is meant for a dark        */
/*             ground; a light plate would erase it.                     */
/* ------------------------------------------------------------------ */

const defaultPartners = [
    /* Diamond */
    { name: 'RealSoft', logo: '/images/partners/RealSoft.png', tier: 'diamond', tone: 'plate', href: '' },

    /* Emerald */
    { name: 'JIOSK', logo: '/images/partners/JIOSK-logo.png', tier: 'emerald', tone: 'dark', href: '' },

    /* Platinum */
    { name: 'IEEE Computational Intelligence Society', logo: '/images/partners/Ieeecis.png', tier: 'platinum', tone: 'dark', href: '' },
    { name: 'Smart Cell', logo: '/images/partners/smartCellLogoWTxt.png', tier: 'platinum', tone: 'plate', href: 'https://ses-jo.com/' },

    /* Gold */
    { name: 'Replit', logo: '/images/partners/Rrplit.png', tier: 'gold', tone: 'plate', href: '' },
    { name: 'Pioneers Group', logo: '/images/partners/pioneers.png', tier: 'gold', tone: 'dark', href: '' },
    { name: 'GitReady', logo: '/images/partners/GitReady.png', tier: 'gold', tone: 'dark', href: '' },
    { name: 'Future to BI', logo: '/images/partners/futurelogo.png', tier: 'gold', tone: 'plate', href: '' },

    /* Silver — MCCLogo.png is a white mark saved onto an opaque WHITE
       background (≈85% of its pixels are #FFFFFF and the strokes sit within
       two levels of it), so it renders as either a blank white box or nothing
       at all. Until a usable file lands, the tile falls back to the name,
       which the slot renderer already supports. Drop in a transparent or
       dark-ink version and just restore the logo path here.
       AiHub.png is a WHITE wordmark on alpha, so it needs the dark ground —
       a light plate would erase it. */
    { name: 'MCC', logo: '/images/partners/MCCLogo.png', tier: 'silver', href: '' },
    { name: 'Shans', logo: '/images/partners/Shanes.png', tier: 'silver', tone: 'dark', href: '' },
    { name: 'Uniflix', logo: '/images/partners/Unflix.png', tier: 'silver', tone: 'plate', href: '' },
    { name: 'AI Hub', logo: '/images/partners/AiHub.png', tier: 'silver', tone: 'dark', href: '' },
    /* Apex — dark teal Arabic wordmark on an opaque white canvas, so it needs
       the plate. */
    { name: 'Apex', logo: '/images/partners/Apex.png', tier: 'gold', tone: 'plate', href: '' },

    /* Bronze — Stakarz is a chrome/silver mark and Fourth Dimension a solid
       green one, both on alpha: they belong on the dark ground. */
    { name: 'IEEE UJ Student Branch', logo: '/images/partners/ieeeujBranch.png', tier: 'bronze', tone: 'plate', href: '' },
    { name: 'Stakarz', logo: '/images/partners/stakarz.png', tier: 'bronze', tone: 'dark', href: '' },
    { name: 'Fourth Dimension for Plastic Industry', logo: '/images/partners/FourthDimensionForPlasticIndustryLTD.png', tier: 'bronze', tone: 'dark', href: '' },
];

/* ------------------------------------------------------------------ */
/* Hooks                                                               */
/* ------------------------------------------------------------------ */

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const useSpotlight = (ref) => {
    useEffect(() => {
        const el = ref.current;
        if (!el || prefersReducedMotion()) return;

        let raf = 0;
        let pending = null;

        const onMove = (e) => {
            const rect = el.getBoundingClientRect();
            pending = {
                x: ((e.clientX - rect.left) / rect.width) * 100,
                y: ((e.clientY - rect.top) / rect.height) * 100,
            };
            if (!raf) {
                raf = requestAnimationFrame(() => {
                    if (pending) {
                        el.style.setProperty('--mx', pending.x + '%');
                        el.style.setProperty('--my', pending.y + '%');
                    }
                    raf = 0;
                });
            }
        };

        el.addEventListener('mousemove', onMove);
        return () => {
            el.removeEventListener('mousemove', onMove);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [ref]);
};

/* ------------------------------------------------------------------ */
/* Slot renderer — confirmed partners only                             */
/* ------------------------------------------------------------------ */

const renderSlot = (p, i, tierKey) => {
    const Wrapper = p.href ? 'a' : 'div';
    const wrapperProps = p.href
        ? { href: p.href, target: '_blank', rel: 'noopener noreferrer' }
        : {};

    return (
        <Wrapper
            {...wrapperProps}
            className={`ptn__slot ptn__slot--filled ptn__slot--${tierKey}`}
            key={`${tierKey}-p-${i}`}
            title={p.name}
        >
            <span className="ptn__slot-corner ptn__slot-corner--tl" />
            <span className="ptn__slot-corner ptn__slot-corner--tr" />
            <span className="ptn__slot-corner ptn__slot-corner--bl" />
            <span className="ptn__slot-corner ptn__slot-corner--br" />

            <span className="ptn__slot-sweep" aria-hidden />

            <div className="ptn__slot-bay">
                <span className="ptn__slot-bay-id">
                    BAY/{String(i + 1).padStart(2, '0')}
                </span>
                <span className="ptn__slot-bay-status">
                    <span className="ptn__slot-bay-led" />
                    CONFIRMED
                </span>
            </div>

            <div className={`ptn__slot-content${p.tone === 'plate' ? ' ptn__slot-content--plate' : ''}`}>
                {p.logo ? (
                    <img className="ptn__slot-logo" src={p.logo} alt={p.name} loading="lazy" />
                ) : (
                    <span className="ptn__slot-name">{p.name}</span>
                )}
            </div>

            <div className="ptn__slot-foot">
                <span className="ptn__slot-foot-name">{p.name}</span>
                {p.href && (
                    <span className="ptn__slot-foot-arrow" aria-hidden>
                        <svg viewBox="0 0 16 16" fill="none">
                            <path d="M3 13L13 3M13 3H6M13 3v7"
                                stroke="currentColor"
                                strokeWidth="1.4"
                                strokeLinecap="square" />
                        </svg>
                    </span>
                )}
            </div>
        </Wrapper>
    );
};

/* ------------------------------------------------------------------ */
/* Named-role partner — same slot vocabulary as a tier slot, but with  */
/* no rung around it: the role is printed under the name instead.      */
/* ------------------------------------------------------------------ */

const RolePartner = ({ partner, index }) => {
    const Wrapper = partner.href ? 'a' : 'div';
    const wrapperProps = partner.href
        ? { href: partner.href, target: '_blank', rel: 'noopener noreferrer' }
        : {};

    return (
        <Wrapper
            {...wrapperProps}
            className="ptn__slot ptn__slot--filled ptn__role-slot"
            title={`${partner.name} — ${partner.role}`}
            style={{ '--i': index }}
        >
            <span className="ptn__slot-corner ptn__slot-corner--tl" />
            <span className="ptn__slot-corner ptn__slot-corner--tr" />
            <span className="ptn__slot-corner ptn__slot-corner--bl" />
            <span className="ptn__slot-corner ptn__slot-corner--br" />
            <span className="ptn__slot-sweep" aria-hidden />

            <div className={`ptn__slot-content${partner.tone === 'plate' ? ' ptn__slot-content--plate' : ''}`}>
                {partner.logo ? (
                    <img className="ptn__slot-logo" src={partner.logo} alt={partner.name} loading="lazy" />
                ) : (
                    <span className="ptn__slot-name">{partner.name}</span>
                )}
            </div>

            <div className="ptn__slot-foot">
                <span className="ptn__slot-foot-name">{partner.name}</span>
                {partner.href && (
                    <span className="ptn__slot-foot-arrow" aria-hidden>
                        <svg viewBox="0 0 16 16" fill="none">
                            <path d="M3 13L13 3M13 3H6M13 3v7"
                                stroke="currentColor"
                                strokeWidth="1.4"
                                strokeLinecap="square" />
                        </svg>
                    </span>
                )}
            </div>

            {/* The role, written under the partner rather than over a group */}
            <div className="ptn__role-tag">
                <span className="ptn__role-tag-dot" aria-hidden />
                {partner.role}
            </div>
        </Wrapper>
    );
};

/* ------------------------------------------------------------------ */
/* Tier row — one rung of the pyramid (confirmed partners only)        */
/* ------------------------------------------------------------------ */

const TierRow = ({ tier, partners, role = false }) => (
    <div
        className={`ptn__row ptn__row--${tier.key}${role ? ' ptn__row--role' : ''}`}
        data-count={partners.length}
    >
        {/* Row header — tier name, serial, hairline, real count */}
        <div className="ptn__row-head">
            <span className="ptn__row-head-l">
                <span className="ptn__row-head-tick" />
                <span className="ptn__row-head-serial">{tier.serial}</span>
                <span className="ptn__row-head-divider" />
                <span className="ptn__row-head-label">{tier.label}</span>
            </span>
            <span className="ptn__row-head-line" />
            <span className="ptn__row-head-r">
                <span className="ptn__row-head-count">
                    {String(partners.length).padStart(2, '0')}&nbsp;confirmed
                </span>
            </span>
        </div>

        {/* Row body — the confirmed slots */}
        <div className="ptn__row-body">
            <span className="ptn__row-body-cap ptn__row-body-cap--l" aria-hidden>
                <span className="ptn__row-body-cap-corner ptn__row-body-cap-corner--tl" />
                <span className="ptn__row-body-cap-corner ptn__row-body-cap-corner--bl" />
            </span>
            <span className="ptn__row-body-cap ptn__row-body-cap--r" aria-hidden>
                <span className="ptn__row-body-cap-corner ptn__row-body-cap-corner--tr" />
                <span className="ptn__row-body-cap-corner ptn__row-body-cap-corner--br" />
            </span>

            <div className="ptn__row-slots">
                {partners.map((p, i) => renderSlot(p, i, tier.key))}
            </div>
        </div>
    </div>
);

/* ------------------------------------------------------------------ */
/* Section                                                              */
/* ------------------------------------------------------------------ */

const Partners = ({ partners = defaultPartners }) => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useSpotlight(ref);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver((entries) => {
            entries.forEach(e => {
                if (e.isIntersecting) {
                    setVisible(true);
                    obs.unobserve(el);
                }
            });
        }, { threshold: 0.18 });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    // Group partners by tier
    const grouped = TIERS.reduce((acc, tier) => {
        acc[tier.key] = partners.filter(p => p.tier === tier.key);
        return acc;
    }, {});

    // Only render rows that actually have confirmed partners — open tiers are
    // represented by the "opportunities available" invitation below.
    const filledTiers = TIERS.filter(t => grouped[t.key].length > 0);

    return (
        <section
            ref={ref}
            className={`ptn${visible ? ' is-visible' : ''}`}
            id="partners"
            aria-labelledby="ptn-title"
        >
            {/* Background layers */}
            <div className="ptn__grid"  aria-hidden />
            <div className="ptn__spot"  aria-hidden />
            <div className="ptn__noise" aria-hidden />

            <div className="ptn__wrap">

                {/* HUD STRIP */}
                <div className="ptn__hud" aria-hidden>
                    <span className="ptn__hud-tag">
                        <span className="ptn__hud-dot" />
                        SEC&nbsp;//&nbsp;05
                    </span>
                    <span className="ptn__hud-line" />
                    <span className="ptn__hud-mid">
                        SUPPORTED&nbsp;BY&nbsp;·&nbsp;
                        {partners.length === 0
                            ? 'ROSTER PENDING'
                            : `${String(partners.length).padStart(2, '0')} CONFIRMED`}
                    </span>
                    <span className="ptn__hud-line" />
                    <span className="ptn__hud-tag ptn__hud-tag--right">
                        EDITION&nbsp;01
                        <span className="ptn__hud-blink" />
                    </span>
                </div>

                {/* HEADER */}
                <header className="ptn__head">
                    <div className="ptn__head-left">
                        <div className="ptn__eyebrow">
                            <span className="ptn__eyebrow-bracket">[</span>
                            <span>Sponsors &amp; partners</span>
                            <span className="ptn__eyebrow-bracket">]</span>
                        </div>

                        <h2 className="ptn__title" id="ptn-title">
                            <span className="ptn__title-row">
                                <span className="ptn__title-num" aria-hidden>04</span>
                                <span className="ptn__title-text">Supported</span>
                            </span>
                            <span className="ptn__title-row ptn__title-row--alt">
                                <span className="ptn__title-text ptn__title-text--outline">
                                    by.
                                </span>
                            </span>
                        </h2>
                    </div>

                    <div className="ptn__head-right">
                        <div className="ptn__lede-wrap">
                            <span className="ptn__lede-bar" aria-hidden />
                            <p className="ptn__lede">
                                AI Expo Jordan 2026 is built in partnership with the
                                companies, universities, and institutions that believe
                                Jordan can lead the regional AI conversation.
                            </p>
                        </div>

                        {/* Tier legend — pyramid order, top → bottom */}
                        <div className="ptn__tiers">
                            <span className="ptn__tiers-label">PYRAMID&nbsp;//</span>
                            {TIERS.map((t) => (
                                <span
                                    key={t.key}
                                    className={`ptn__tier ptn__tier--${t.key}`}
                                >
                                    <span className="ptn__tier-dot" />
                                    {t.label.toUpperCase()}
                                </span>
                            ))}
                        </div>
                    </div>
                </header>

                {/* ENDORSEMENTS — sit above the pyramid / strategic partners */}
                <div className="ptn__endorsements">
                    {/* <div className="ptn__endorse">
                        <span className="ptn__endorse-corner ptn__endorse-corner--tl" />
                        <span className="ptn__endorse-corner ptn__endorse-corner--tr" />
                        <span className="ptn__endorse-corner ptn__endorse-corner--bl" />
                        <span className="ptn__endorse-corner ptn__endorse-corner--br" />
                        <span className="ptn__endorse-label">Under the patronage of</span>
                        <div className="ptn__endorse-logo-wrap">
                            <img
                                className="ptn__endorse-logo"
                                src="/images/partners/uj.webp"
                                alt="University of Jordan"
                                loading="lazy"
                                decoding="async"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                        </div>
                    </div> */}
                    <div className="ptn__endorse">
                        <span className="ptn__endorse-corner ptn__endorse-corner--tl" />
                        <span className="ptn__endorse-corner ptn__endorse-corner--tr" />
                        <span className="ptn__endorse-corner ptn__endorse-corner--bl" />
                        <span className="ptn__endorse-corner ptn__endorse-corner--br" />
                        <span className="ptn__endorse-label">In collaboration with</span>
                        <div className="ptn__endorse-logo-wrap">
                            <img
                                className="ptn__endorse-logo"
                                src="/images/partners/digital-economy.webp"
                                alt="Ministry of Digital Economy and Entrepreneurship"
                                loading="lazy"
                                decoding="async"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                        </div>
                    </div>
                </div>

                {/* PYRAMID — confirmed tiers only */}
                <div className="ptn__pyramid">
                    {filledTiers.map((tier) => (
                        <TierRow
                            key={tier.key}
                            tier={tier}
                            partners={grouped[tier.key]}
                        />
                    ))}
                </div>

                {/* NAMED ROLES — no rungs; each partner states its own role */}
                {ROLE_PARTNERS.length > 0 && (
                    <div className="ptn__roles-group">
                        <div className="ptn__roles-label">
                            <span className="ptn__roles-label-tick" aria-hidden />
                            Partners by role
                            <span className="ptn__roles-label-line" aria-hidden />
                        </div>

                        <div className="ptn__roles-wall">
                            {ROLE_PARTNERS.map((p, i) => (
                                <RolePartner key={p.name} partner={p} index={i} />
                            ))}
                        </div>
                    </div>
                )}

                {/* PARTNERSHIP OPPORTUNITIES — honest invitation + CTA */}
                <div className="ptn__invite">
                    <span className="ptn__invite-led" aria-hidden />
                    <p className="ptn__invite-text">
                        <b>Partnership opportunities available.</b> Places remain open across
                        every tier for your organization.
                    </p>
                </div>

                {/* OUTREACH — logo wall, shared with the /partners page */}
                <OutreachPartners id="outreach-home" />

                <footer className="ptn__close">
                    <Link to="/contact" className="ptn__cta">
                        <span className="ptn__cta-bracket">[</span>
                        <span className="ptn__cta-text">Become a partner</span>
                        <span className="ptn__cta-arrow">
                            <svg viewBox="0 0 24 12" fill="none" aria-hidden>
                                <path d="M1 6h22m0 0L18 1m5 5l-5 5"
                                    stroke="currentColor" strokeWidth="1.4"
                                    strokeLinecap="square" />
                            </svg>
                        </span>
                        <span className="ptn__cta-bracket">]</span>
                    </Link>
                </footer>
            </div>
        </section>
    );
};

export default Partners;
